<i18n>
{
  "de": {
    "noOfferingsTitle": "Noch nichts im Shop",
    "noOfferingsMessage": "Schau später noch einmal vorbei",
    "allCategories": "Alle"
  },
  "en": {
    "noOfferingsTitle": "Nothing in the shop yet",
    "noOfferingsMessage": "Check back later",
    "allCategories": "All"
  }
}
</i18n>

<template>
  <div class="shop-content">
    <!-- Empty State -->
    <div
      v-if="!hasAnyOfferings"
      class="shop-content__empty"
    >
      <div class="shop-content__empty-box">
        <div class="shop-content__empty-icon">
          🛍️
        </div>
        <h2 class="shop-content__empty-title">
          {{ $t('noOfferingsTitle') }}
        </h2>
        <p class="shop-content__empty-message">
          {{ $t('noOfferingsMessage') }}
        </p>
      </div>
    </div>

    <!-- Content Area -->
    <div v-else>
      <!-- Category Bar (Desktop only, Products type only) -->
      <div
        v-if="showCategoryBar"
        class="shop-content__category-bar"
      >
        <button
          class="category-chip"
          :class="{ 'category-chip--active': !activeCategory }"
          @click="setCategory(null)"
        >
          {{ $t('allCategories') }}
        </button>
        <button
          v-for="category in availableCategories"
          :key="category.slug"
          class="category-chip"
          :class="{ 'category-chip--active': activeCategory === category.slug }"
          @click="setCategory(category.slug)"
        >
          {{ category.name }}
        </button>
      </div>

      <!-- Unified Shop Grid -->
      <div class="shop-content__grid">
        <template v-for="item in sortedItems">
          <subscription-card
            v-if="item.type === 'subscription'"
            :key="item.id"
            :reward="item.originalItem"
          />
          <product-card
            v-else-if="item.type === 'product'"
            :key="item.id"
            :product="item.originalItem"
          />
          <event-card
            v-else-if="item.type === 'event'"
            :key="item.id"
            :event="item.originalItem"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import SubscriptionCard from './subscription-card.vue'
import ProductCard from './product-card.vue'
import EventCard from './event-card.vue'

export default {
  name: 'ShopContent',

  components: {
    SubscriptionCard,
    ProductCard,
    EventCard
  },

  props: {
    categoryFilter: {
      type: String,
      default: null
    }
  },

  computed: {
    ...mapState(['artist', 'shopFilter']),

    // Get filter values from Vuex state
    filterType() {
      return this.shopFilter?.type || null
    },
    sortBy() {
      return this.shopFilter?.sortBy || 'date'
    },
    sortOrder() {
      return this.shopFilter?.sortOrder || 'desc'
    },

    // Build unified list of all shop items
    allShopItems() {
      const items = []
      const artistId = this.artist?._id

      // Add subscriptions (rewards)
      if (this.artist?.rewards) {
        this.artist.rewards.forEach(reward => {
          items.push({
            id: `subscription-${reward._id}`,
            type: 'subscription',
            originalItem: reward,
            price: reward.amount || 0,
            date: this.parseDate(reward.publishDateTime || reward.createdAt),
            title: reward.name,
            image: this.buildImageUrl(reward.image, artistId, 'reward')
          })
        })
      }

      // Add products
      if (this.artist?.products) {
        this.artist.products.forEach(product => {
          items.push({
            id: `product-${product._id}`,
            type: 'product',
            originalItem: product,
            price: product.minAmount || product.amount || 0,
            date: this.parseDate(product.publishDateTime || product.createdAt),
            title: product.name,
            image: this.buildImageUrl(product.image, artistId, 'product')
          })
        })
      }

      // Add events
      if (this.artist?.events) {
        this.artist.events.forEach(event => {
          const lowestPrice = this.getEventLowestPrice(event)
          items.push({
            id: `event-${event.id}`,
            type: 'event',
            originalItem: event,
            price: lowestPrice,
            date: this.parseDate(event.startDate),
            title: event.title,
            image: this.buildEventImageUrl(event.coverImage, artistId)
          })
        })
      }

      return items
    },

    // Filter items by type and category if specified
    filteredItems() {
      let items = this.allShopItems

      // Filter by type
      if (this.filterType) {
        items = items.filter(item => item.type === this.filterType)
      }

      // Filter by category (only applies to products)
      if (this.categoryFilter) {
        items = items.filter(item => {
          if (item.type !== 'product') return false
          const categories = item.originalItem.categories || []
          return categories.some(cat => cat.slug === this.categoryFilter)
        })
      }

      return items
    },

    // Sort items based on sortBy and sortOrder
    sortedItems() {
      const items = [...this.filteredItems]

      items.sort((a, b) => {
        let comparison = 0

        if (this.sortBy === 'price') {
          comparison = a.price - b.price
        } else if (this.sortBy === 'name') {
          // Alphabetical sorting by title
          const titleA = (a.title || '').toLowerCase()
          const titleB = (b.title || '').toLowerCase()
          comparison = titleA.localeCompare(titleB)
        } else if (this.sortBy === 'date') {
          // Handle null dates - put items without dates at the end
          if (!a.date && !b.date) comparison = 0
          else if (!a.date) comparison = 1
          else if (!b.date) comparison = -1
          else comparison = a.date.getTime() - b.date.getTime()
        }

        return this.sortOrder === 'asc' ? comparison : -comparison
      })

      return items
    },

    // Has content checks
    hasAnyOfferings() {
      return this.allShopItems.length > 0
    },

    // Extract unique categories from all products
    availableCategories() {
      const categories = new Map()
      for (const product of this.artist?.products || []) {
        for (const cat of product.categories || []) {
          if (cat.slug && cat.name) {
            categories.set(cat.slug, { name: cat.name, slug: cat.slug })
          }
        }
      }
      return Array.from(categories.values())
    },

    // Get the currently active category from Vuex or route
    activeCategory() {
      // Check route param first (for direct URL access)
      if (this.$route.params.category) {
        return this.$route.params.category
      }
      // Fall back to Vuex state
      return this.shopFilter?.category || null
    },

    // Show category bar on desktop for "All" and "Products" views when categories exist
    showCategoryBar() {
      return (
        !this.isMobileView &&
        (!this.filterType || this.filterType === 'product') &&
        this.availableCategories.length > 0
      )
    },

    // Build route path for category navigation
    shopBasePath() {
      return `/${this.artist?.urlAlias}/shop`
    }
  },

  methods: {
    ...mapActions(['setShopFilter']),

    // Navigate to category
    setCategory(slug) {
      if (slug) {
        // Navigate to specific category page
        const targetPath = `${this.shopBasePath}/products/${slug}`
        if (this.$route.path !== targetPath) {
          this.$router.push(targetPath)
        }
      } else {
        // Navigate back to all products
        const targetPath = `${this.shopBasePath}/products`
        if (this.$route.path !== targetPath) {
          this.$router.push(targetPath)
        }
      }
      // Also update Vuex for consistency
      this.setShopFilter({ category: slug })
    },

    // Parse date string to Date object
    parseDate(dateString) {
      if (!dateString) return null
      const date = new Date(dateString)
      return isNaN(date.getTime()) ? null : date
    },

    // Build image URL for rewards and products
    buildImageUrl(imageObj, artistId, type) {
      if (!imageObj || !artistId) return null

      // Legacy string format (direct URL)
      if (typeof imageObj === 'string') {
        return imageObj
      }

      // Standard image object with name property
      if (imageObj.name) {
        return `/services/media/image/${artistId}/${imageObj.name}/600/0/image.webp`
      }

      return null
    },

    // Build image URL for events (different media service)
    buildEventImageUrl(coverImage, artistId) {
      if (!coverImage?.id || !artistId) return null
      return `/services/media/ticketier/image/${artistId}/${coverImage.id}/600/0/image.webp`
    },

    // Get lowest price from event ticket categories
    getEventLowestPrice(event) {
      const activeCategories = event?.ticketCategories?.filter(c => c.active) || []
      if (!activeCategories.length) return 0
      return Math.min(...activeCategories.map(c => c.priceInCents || 0))
    }
  }
}
</script>

<style lang="scss" scoped>
@import "assets/sass/_variables";

.shop-content {
  padding-bottom: 100px;

  // Desktop only
  @media screen and (min-width: #{$desktop}) {
    // Ensures consistent height for scroll position maintenance (58px = top menu bar)
    min-height: calc(100vh - 58px);
  }

  // Category Bar (Desktop only)
  &__category-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 24px;
    padding: 16px 0;
    border-bottom: 1px solid color-mix(
      in hsl,
      var(--body-background-color),
      var(--body-background-text-color) 15%
    );

    @media screen and (max-width: #{$mobile}) {
      display: none;
    }
  }

  // Unified Grid
  &__grid {
    display: grid;
    gap: 20px;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }

  // Empty State
  &__empty {
    padding: 60px 20px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__empty-box {
    text-align: center;
    max-width: 320px;
    padding: 40px;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 16px;
    border: 2px dashed rgba(0, 0, 0, 0.1);
  }

  &__empty-icon {
    font-size: 3rem;
    margin-bottom: 16px;
  }

  &__empty-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--secondary-color, #333);
    margin: 0 0 8px 0;
  }

  &__empty-message {
    font-size: 0.95rem;
    color: var(--secondary-color, #666);
    opacity: 0.7;
    margin: 0;
  }
}

// Category Chip Styles
.category-chip {
  position: relative;
  padding: 10px 20px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  border-radius: 5px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  // Default state - semi-transparent with text color
  background: color-mix(
    in hsl,
    var(--body-background-color),
    var(--body-background-text-color) 8%
  );
  color: var(--body-background-text-color);

  // Subtle inner glow effect
  box-shadow:
    inset 0 0 0 1px color-mix(
      in hsl,
      var(--body-background-color),
      var(--body-background-text-color) 15%
    );

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      inset 0 0 0 1px color-mix(
        in hsl,
        var(--body-background-color),
        var(--body-background-text-color) 25%
      ),
      0 4px 12px color-mix(
        in hsl,
        var(--body-background-color),
        black 15%
      );
  }

  // Active state - filled with primary color
  &--active {
    background: var(--primary-color);
    color: var(--primary-text-color);
    box-shadow:
      0 4px 15px color-mix(
        in hsl,
        var(--primary-color),
        transparent 50%
      );

    &:hover {
      transform: translateY(-2px);
      box-shadow:
        0 6px 20px color-mix(
          in hsl,
          var(--primary-color),
          transparent 40%
        );
    }
  }
}

// Mobile adjustments
@media screen and (max-width: #{$mobile}) {
  .shop-content {
    padding-bottom: 80px;

    &__grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }

    &__empty {
      padding: 40px 16px;
    }

    &__empty-box {
      padding: 30px 20px;
    }

    &__empty-icon {
      font-size: 2.5rem;
    }

    &__empty-title {
      font-size: 1.1rem;
    }

    &__empty-message {
      font-size: 0.9rem;
    }
  }
}

// Tablet: 2 columns
@media screen and (min-width: #{$mobile}) and (max-width: #{$desktop}) {
  .shop-content {
    &__grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}
</style>
